import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo } from 'react'
import { getSingleQuery, stringifyUrl } from '@ucheba/utils/helpers/router'
import { useDispatch, useSelector } from 'react-redux'
import { clientProductsTariffsSelectors } from '@ucheba/store/client-products/tariffs'
import {
  IProduct,
  IProductByClass,
  IProductsByType,
} from '@ucheba/store/client-products/tariffs/types'
import { examsSelectors, examsThunks } from '@ucheba/store/exams'
import { isBrowser } from '@ucheba/utils/helpers/core'

interface IUseSchoolPriceBlock {
  (
    type?: 'ege' | 'oge',
    initialClass?: string
  ): {
    onSelectDiscount: (key: number) => void
    onSelectType: (type: string) => void
    activeCountIndex: number
    onSelectClass: (className: string) => void
    activeProductClass: IProductByClass | null
    productTypes: IProductsByType[]
    activeProductType: IProductsByType | null
    products: IProductsByType['products']
    subjectCounts: Array<string | number>[]
    discount: number | null
    baseDiscount: number
    productsByClass?: IProductByClass[] | null
    onPay: (product: IProduct) => void
  }
}

export const useSchoolPriceBlock: IUseSchoolPriceBlock = (type, initialClass) => {
  const { query, push, pathname } = useRouter()
  const entity = useSelector(clientProductsTariffsSelectors.entity)
  const productsByClassEntity = entity?.productsByClass
  const exams = useSelector(examsSelectors.selectAll)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(examsThunks.fetch({}))
  }, [])

  const productsByClass = useMemo(() => {
    return productsByClassEntity?.filter((product) => {
      if (type === 'oge') {
        return product.schoolClass.value === '9'
      }

      if (type === 'ege') {
        return product.schoolClass.value !== '9'
      }

      return true
    })
  }, [type, productsByClassEntity])

  const onSelectDiscount = useCallback(
    (key) => {
      push(
        stringifyUrl(pathname, {
          update: {
            ...query,
            count: key,
          },
        }),
        undefined,
        {
          shallow: true,
        }
      )
    },
    [pathname, push]
  )

  const onSelectType = useCallback(
    (t) => {
      push(
        stringifyUrl(pathname, {
          update: {
            ...query,
            type: t,
          },
        }),
        undefined,
        {
          shallow: true,
        }
      )
    },
    [pathname, push, query]
  )

  const activeCountIndex = useMemo(() => {
    if (!query.count) return 0

    return Number(query.count) - 1
  }, [query])

  const activeProductClass = useMemo(() => {
    if (!productsByClass) return null

    if (query.class) {
      return (
        productsByClass.find((el) => el.schoolClass.value === query.class) ||
        productsByClass[0] ||
        null
      )
    }

    if (initialClass) {
      return (
        productsByClass.find((el) => el.schoolClass.value === initialClass) ||
        productsByClass[0] ||
        null
      )
    }

    return productsByClass[0] || null
  }, [query, productsByClass, initialClass])

  const productTypes = useMemo(() => {
    if (activeProductClass) {
      return activeProductClass?.productsByTypeAndDuration
    }

    return []
  }, [activeProductClass])

  const activeProductType = useMemo(() => {
    if (!productTypes) return null

    if (query.type) {
      const productType = productTypes.find((el) => el.name === query.type) || null

      if (productType) {
        return productType
      }
    }

    return productTypes[productTypes.length - 1 || 0] || null
  }, [productTypes, query])

  const products = useMemo(() => {
    if (!activeProductType) return []

    return activeProductType.products
  }, [activeProductType])

  const subjectCounts = useMemo(() => {
    if (!products) return []

    let res: any = []

    const countsArray = products.map((el) => el.byCountDiscounts)

    countsArray.forEach((dis) => {
      const arr = Object.entries(dis)
      if (res.length < arr.length) {
        res = arr
      }
    })

    return res
  }, [products])

  const discount = useMemo(() => {
    if (subjectCounts[activeCountIndex]) {
      return subjectCounts[activeCountIndex][1] || null
    }

    return null
  }, [activeCountIndex, subjectCounts])

  const onSelectClass = useCallback(
    (className) => {
      const params = query
      const product =
        productsByClass?.find((el) => el.schoolClass.value === className) || null
      if (product && product.productsByTypeAndDuration.length > 0) {
        params.type =
          product.productsByTypeAndDuration[
            product.productsByTypeAndDuration.length - 1 || 0
          ].name
      }

      push(
        stringifyUrl(pathname, {
          update: {
            ...params,
            class: className,
          },
        }),
        undefined,
        {
          shallow: true,
        }
      )
    },
    [query, pathname, push, productsByClass]
  )

  useEffect(() => {
    if (query.count && subjectCounts) {
      const hasCountInClass = subjectCounts.some((item) => {
        return String(item[0]) === String(query.count)
      })

      if (!hasCountInClass) {
        push(
          stringifyUrl(pathname, {
            update: {
              ...query,
            },
            exclude: ['count'],
          }),
          undefined,
          {
            shallow: true,
          }
        )
      }
    }
  }, [subjectCounts])

  useEffect(() => {
    if (query.type && activeProductType && activeProductType.name !== query.type) {
      push(
        stringifyUrl(pathname, {
          update: {
            ...query,
            type: activeProductType.name,
          },
        }),
        undefined,
        {
          shallow: true,
        }
      )
    }
  }, [activeProductType, query])

  const baseDiscount = useMemo(() => {
    if (products) {
      return products.find((el) => el.fullPaymentDiscount)?.fullPaymentDiscount || 0
    }

    return 0
  }, [products])

  const getProductTypeValue = (
    item: IProductsByType,
    inMonthDuration?: boolean
  ): string => {
    if (inMonthDuration) {
      return `${item.type.value}-${item.durationInMonth || ''}-${item.name}-in_month`
    }

    return `${item.type.value}-${item.durationInMonth || ''}-${item.name}`
  }

  const onPay = useCallback(
    (product: IProduct) => {
      if (activeProductClass && activeProductType) {
        const slug = getSingleQuery(query.subject)

        const subject = exams.find((el) => el.subject.slug === slug)

        const data = {
          classValue: activeProductClass.schoolClass.value,
          paymentProductType: getProductTypeValue(activeProductType, false),
          accessLevel: product.educationAccessLevel.value,
        } as Record<string, any>

        if (subject) {
          data.subjectIds = [subject.id]
        }

        const url = stringifyUrl('/payment', {
          set: {
            ...data,
          },
        })

        const NEXT_PUBLIC_URL = process.env.NEXT_PUBLIC_URL || ''

        let host = ''

        if (NEXT_PUBLIC_URL.includes('dev2')) {
          host += 'https://edu.dev2.ucheba.space'
        } else if (NEXT_PUBLIC_URL.includes('dev')) {
          host += 'https://edu.dev.ucheba.space'
        } else {
          host += 'https://school.ucheba.ru'
        }

        if (isBrowser) {
          window.location.href = host + url
        } else {
          push(host + url)
        }
      }
    },
    [activeProductClass, activeProductType, exams, push, query.subject]
  )

  return {
    onSelectDiscount,
    onSelectType,
    activeCountIndex,
    onSelectClass,
    activeProductClass,
    productTypes,
    activeProductType,
    products,
    subjectCounts,
    discount,
    baseDiscount,
    productsByClass,
    onPay,
  }
}
